import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const MAX_RANGE_DAYS = 365;

function toDateOnly(d) {
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
}

function yesterday() {
  return nDaysAgo(1);
}

function nDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateOnly(d);
}

// Report only includes data up to yesterday. Earliest selectable start = yesterday minus 1 year.
function maxReportEnd() {
  return yesterday();
}

function minReportStart() {
  const d = new Date();
  d.setDate(d.getDate() - 1 - MAX_RANGE_DAYS);
  return toDateOnly(d);
}

const DATE_PRESETS = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_1_week', label: 'Last 1 week' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'this_year', label: 'This year' },
];

function getRangeForPreset(presetId) {
  const endStr = maxReportEnd();
  const now = new Date();
  switch (presetId) {
    case 'yesterday':
      return { start: endStr, end: endStr };
    case 'last_1_week':
      return { start: nDaysAgo(7), end: endStr };
    case 'last_30_days':
      return { start: nDaysAgo(30), end: endStr };
    case 'this_year': {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      return { start: toDateOnly(yearStart), end: endStr };
    }
    default:
      return null;
  }
}

export default function CreditAnalytics() {
  const [selectedPreset, setSelectedPreset] = useState('yesterday');
  const [environment, setEnvironment] = useState('production');
  const [start, setStart] = useState(yesterday());
  const [end, setEnd] = useState(yesterday());
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [creditsPerDollar, setCreditsPerDollar] = useState(120);
  const [accountsData, setAccountsData] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState(null);

  const range = { start, end };
  const minStart = minReportStart();
  const maxEnd = maxReportEnd();

  const setPresetAndRange = (presetId) => {
    setSelectedPreset(presetId);
    const rangeForPreset = getRangeForPreset(presetId);
    if (rangeForPreset) {
      setStart(rangeForPreset.start);
      setEnd(rangeForPreset.end);
    }
  };

  const handleStartChange = (value) => {
    setSelectedPreset(null);
    setStart(value);
  };

  const handleEndChange = (value) => {
    setSelectedPreset(null);
    setEnd(value);
  };

  const rangeParams = () => {
    const startDate = new Date(`${range.start}T00:00:00`);
    const endDate = new Date(`${range.end}T23:59:59.999`);

    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      environment,
    };
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const res = await api.get('/api/credit-analytics/stats', {
        params: rangeParams(),
      });
      setStats(res.data?.data || null);
    } catch (err) {
      setStatsError(err.response?.data?.error || err.message || 'Failed to load credit analytics');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [range.start, range.end, environment]);

  const fetchAccountsAndCredits = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    setAccountsData([]);
    try {
      const params = rangeParams();
      const accountsRes = await api.get('/api/credit-analytics/accounts', { params });
      const list = accountsRes.data?.data?.accounts || [];
      setAccountsData(list);
    } catch (err) {
      setAccountsError(err.response?.data?.error || err.message || 'Failed to load credits by account');
      setAccountsData([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [range.start, range.end, environment]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchAccountsAndCredits();
  }, [fetchAccountsAndCredits]);

  const credits = stats?.credits || 0;
  const revenue = creditsPerDollar > 0 ? credits / creditsPerDollar : 0;

  const handleCreditsPerDollarChange = (e) => {
    const value = parseFloat(e.target.value);
    if (Number.isNaN(value) || value <= 0) {
      setCreditsPerDollar(0);
      return;
    }
    setCreditsPerDollar(value);
  };

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#1f2937',
      }}
    >
      <style>{`
        @keyframes creditAnalyticsSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes creditAnalyticsSkeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .credit-analytics-spinner {
            animation: none !important;
          }
          .credit-analytics-skeleton {
            animation: none !important;
          }
        }
        .credit-analytics-skeleton {
          background: linear-gradient(
            90deg,
            rgba(148, 163, 184, 0.18) 25%,
            rgba(148, 163, 184, 0.32) 37%,
            rgba(148, 163, 184, 0.18) 63%
          );
          background-size: 400% 100%;
          animation: creditAnalyticsSkeleton 1.2s ease-in-out infinite;
          border-radius: 10px;
        }
      `}</style>
      <header
        style={{
          marginBottom: '28px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 600, color: '#111827', letterSpacing: '-0.02em' }}>
          Credit analytics
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#6b7280' }}>
          Credits consumed and estimated revenue for the selected period.
        </p>

        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: '#495057', fontWeight: '600' }}>Environment:</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={() => setEnvironment('production')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: environment === 'production' ? '600' : '400',
                backgroundColor: environment === 'production' ? '#007bff' : 'transparent',
                color: environment === 'production' ? '#fff' : '#495057',
                transition: 'all 0.2s',
                height: '24px',
              }}
              onMouseEnter={(e) => {
                if (environment !== 'production') e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseLeave={(e) => {
                if (environment !== 'production') e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Production
            </button>
            <button
              type="button"
              onClick={() => setEnvironment('staging')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: environment === 'staging' ? '600' : '400',
                backgroundColor: environment === 'staging' ? '#007bff' : 'transparent',
                color: environment === 'staging' ? '#fff' : '#495057',
                transition: 'all 0.2s',
                height: '24px',
              }}
              onMouseEnter={(e) => {
                if (environment !== 'staging') e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseLeave={(e) => {
                if (environment !== 'staging') e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Staging
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginTop: '20px',
            flexWrap: 'wrap',
            padding: '16px 20px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
            <span>From</span>
            <input
              type="date"
              value={start}
              min={minStart}
              max={end}
              onChange={(e) => handleStartChange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#0f172a',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px #3b82f6')}
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
            <span>To</span>
            <input
              type="date"
              value={end}
              min={start}
              max={maxEnd}
              onChange={(e) => handleEndChange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#0f172a',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px #3b82f6')}
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
          </label>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Report period: up to yesterday. Max range 1 year ({minStart} to {maxEnd}).
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          {DATE_PRESETS.map((preset) => {
            const isSelected = selectedPreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setPresetAndRange(preset.id)}
                style={{
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#fff' : '#475569',
                  background: isSelected ? '#0f172a' : '#fff',
                  border: `1px solid ${isSelected ? '#0f172a' : '#e2e8f0'}`,
                  borderRadius: '9999px',
                  cursor: 'pointer',
                  boxShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  transition: 'color 0.15s, background 0.15s, border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </header>

      {statsError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#dc2626',
          }}
        >
          {statsError}
        </div>
      )}

      <section style={{ marginTop: '8px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '20px',
            }}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '20px 22px',
                color: '#0f172a',
                boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
                border: '1px solid #e2e8f0',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px', fontWeight: 500 }}>
                Credits used
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
                {credits.toLocaleString()}
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px' }}>
                Across all workspaces in the selected period.
              </div>
              {statsLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,255,255,0.65)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '18px',
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div className="credit-analytics-skeleton" style={{ height: '14px', width: '42%', marginBottom: '12px' }} />
                    <div className="credit-analytics-skeleton" style={{ height: '34px', width: '60%', marginBottom: '12px' }} />
                    <div className="credit-analytics-skeleton" style={{ height: '12px', width: '78%' }} />
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                      Loading…
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                background: 'linear-gradient(135deg, #ecfdf3, #e0f2fe)',
                borderRadius: '16px',
                padding: '20px 22px',
                color: '#022c22',
                boxShadow: '0 10px 25px rgba(15,23,42,0.1)',
                border: '1px solid rgba(34,197,94,0.35)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ fontSize: '13px', color: '#047857', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>
                <span>$</span>
                <span>Total revenue (approx)</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#047857', lineHeight: 1.1 }}>
                ${revenue.toFixed(2)}
              </div>
              <div style={{ fontSize: '13px', color: '#065f46', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span>@ 1 USD =</span>
                <input
                  type="number"
                  min="1"
                  value={creditsPerDollar}
                  onChange={handleCreditsPerDollarChange}
                  style={{
                    width: '80px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(34,197,94,0.6)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: '#022c22',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#16a34a')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(34,197,94,0.6)')}
                />
                <span>credits</span>
              </div>
              {statsLoading && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '18px',
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div className="credit-analytics-skeleton" style={{ height: '14px', width: '55%', marginBottom: '12px' }} />
                    <div className="credit-analytics-skeleton" style={{ height: '34px', width: '52%', marginBottom: '12px' }} />
                    <div className="credit-analytics-skeleton" style={{ height: '12px', width: '72%' }} />
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                      Loading…
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(accountsLoading || accountsError || accountsData.length > 0) && (
            <div
              style={{
                marginTop: '28px',
                padding: '20px 22px',
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                boxShadow: '0 8px 20px rgba(15,23,42,0.06)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Credits by account
                </h2>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {accountsLoading ? 'Loading rows…' : 'Revenue uses the same USD ↔ credits conversion as the card above.'}
                </span>
              </div>

              {accountsError && (
                <div
                  style={{
                    padding: '10px 12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    marginBottom: '14px',
                    color: '#dc2626',
                    fontSize: '13px',
                  }}
                >
                  {accountsError}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    fontSize: '13px',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          fontWeight: 600,
                          color: '#4b5563',
                          borderBottom: '1px solid #e5e7eb',
                          borderTopLeftRadius: '8px',
                        }}
                      >
                        Account
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '10px 12px',
                          fontWeight: 600,
                          color: '#4b5563',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        Credits
                      </th>
                      <th
                        style={{
                          textAlign: 'right',
                          padding: '10px 12px',
                          fontWeight: 600,
                          color: '#4b5563',
                          borderBottom: '1px solid #e5e7eb',
                          borderTopRightRadius: '8px',
                        }}
                      >
                        Revenue (USD)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountsData.map((row) => {
                      const isLoadingCredits = row.credits === null || row.credits === undefined;
                      const creditsValue = isLoadingCredits ? null : Number(row.credits);
                      const hasCredits = !isLoadingCredits && Number.isFinite(creditsValue);
                      const rowRevenue =
                        creditsPerDollar > 0 && hasCredits ? creditsValue / creditsPerDollar : null;
                      return (
                        <tr key={row.accountId}>
                          <td
                            style={{
                              padding: '10px 12px',
                              borderBottom: '1px solid #f3f4f6',
                              color: '#111827',
                            }}
                          >
                            {row.account || row.accountId}
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              borderBottom: '1px solid #f3f4f6',
                              textAlign: 'right',
                              color: '#111827',
                            }}
                          >
                            {isLoadingCredits ? 'Loading…' : (hasCredits ? creditsValue.toLocaleString() : '—')}
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              borderBottom: '1px solid #f3f4f6',
                              textAlign: 'right',
                              color: '#047857',
                              fontWeight: 500,
                            }}
                          >
                            {isLoadingCredits ? 'Loading…' : (hasCredits ? `$${rowRevenue.toFixed(2)}` : '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {accountsLoading && accountsData.length === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '18px',
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div className="credit-analytics-skeleton" style={{ height: '12px', width: '35%', marginBottom: '12px' }} />
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px', gap: '12px', marginBottom: '10px' }}>
                        <div className="credit-analytics-skeleton" style={{ height: '14px', width: `${70 - (i % 4) * 8}%` }} />
                        <div className="credit-analytics-skeleton" style={{ height: '14px', width: '80%', marginLeft: 'auto' }} />
                        <div className="credit-analytics-skeleton" style={{ height: '14px', width: '85%', marginLeft: 'auto' }} />
                      </div>
                    ))}
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                      Loading…
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
    </div>
  );
}

